import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  Stack,
  IconButton,
  useTheme,
  alpha,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as DoctorIcon,
  CalendarMonth as DateIcon,
  Schedule as TimeIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ja } from 'date-fns/locale';
import { format, parseISO, addHours } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import type { DoctorShift, DoctorShiftCreateRequest, DoctorShiftUpdateRequest } from '../../types/doctorShift';

interface DoctorShiftEditModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: DoctorShiftCreateRequest | DoctorShiftUpdateRequest) => Promise<boolean>;
  shift?: DoctorShift | null;
  isLoading?: boolean;
}

interface FormData {
  doctor_id: string;
  shift_date: Date | null;
  start_time: Date | null;
  end_time: Date | null;
}

interface FormErrors {
  doctor_id?: string;
  shift_date?: string;
  start_time?: string;
  end_time?: string;
  general?: string;
}

/**
 * 医師シフト編集・新規作成モーダルコンポーネント
 * 
 * 意図：
 * - 直感的なフォーム入力体験の提供
 * - リアルタイムバリデーション
 * - 美しいアニメーションとインタラクション
 * 
 * 注意点：
 * - 日時の適切な形式変換
 * - 時間の整合性チェック
 * - エラーハンドリング
 * 
 * 改善提案：
 * - 医師ID選択のオートコンプリート機能
 * - 重複チェックのリアルタイム表示
 * - カレンダーでの日付選択
 */
export const DoctorShiftEditModal: React.FC<DoctorShiftEditModalProps> = ({
  open,
  onClose,
  onSave,
  shift,
  isLoading = false,
}) => {
  const theme = useTheme();
  const isEditMode = !!shift;

  // フォームデータの状態
  const [formData, setFormData] = useState<FormData>({
    doctor_id: '',
    shift_date: null,
    start_time: null,
    end_time: null,
  });

  // エラー状態
  const [errors, setErrors] = useState<FormErrors>({});

  // フォームの初期化
  useEffect(() => {
    if (open) {
      if (shift) {
        // 編集モード: 既存データを設定
        const shiftDate = parseISO(shift.shift_date);
        const startTime = new Date(`2000-01-01T${shift.start_time}`);
        const endTime = new Date(`2000-01-01T${shift.end_time}`);

        setFormData({
          doctor_id: shift.doctor_id,
          shift_date: shiftDate,
          start_time: startTime,
          end_time: endTime,
        });
      } else {
        // 新規作成モード: 初期値を設定
        const today = new Date();
        const defaultStartTime = new Date();
        defaultStartTime.setHours(9, 0, 0, 0);
        const defaultEndTime = new Date();
        defaultEndTime.setHours(17, 0, 0, 0);

        setFormData({
          doctor_id: '',
          shift_date: today,
          start_time: defaultStartTime,
          end_time: defaultEndTime,
        });
      }
      setErrors({});
    }
  }, [open, shift]);

  // バリデーション
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // 医師IDの検証
    if (!formData.doctor_id.trim()) {
      newErrors.doctor_id = '医師IDは必須です';
    } else if (!/^[A-Z0-9]+$/.test(formData.doctor_id)) {
      newErrors.doctor_id = '医師IDは英数字（大文字）で入力してください';
    }

    // シフト日付の検証
    if (!formData.shift_date) {
      newErrors.shift_date = 'シフト日付は必須です';
    }

    // 開始時間の検証
    if (!formData.start_time) {
      newErrors.start_time = '開始時間は必須です';
    }

    // 終了時間の検証
    if (!formData.end_time) {
      newErrors.end_time = '終了時間は必須です';
    }

    // 時間の整合性チェック
    if (formData.start_time && formData.end_time) {
      if (formData.start_time >= formData.end_time) {
        newErrors.end_time = '終了時間は開始時間より後に設定してください';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // フォーム送信
  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      const submitData = {
        doctor_id: formData.doctor_id.trim().toUpperCase(),
        shift_date: format(formData.shift_date!, 'yyyy-MM-dd'),
        start_time: format(formData.start_time!, 'HH:mm'),
        end_time: format(formData.end_time!, 'HH:mm'),
      };

      const success = await onSave(submitData);
      if (success) {
        onClose();
      }
    } catch (error) {
      setErrors({
        general: error instanceof Error ? error.message : '保存に失敗しました'
      });
    }
  };

  // フィールド変更ハンドラー
  const handleFieldChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // エラーをクリア
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // 終了時間の自動調整（開始時間変更時）
  const handleStartTimeChange = (value: Date | null) => {
    handleFieldChange('start_time', value);
    
    // 開始時間が設定され、終了時間が開始時間より前の場合、終了時間を調整
    if (value && formData.end_time && value >= formData.end_time) {
      const newEndTime = addHours(value, 1); // 1時間後に設定
      handleFieldChange('end_time', newEndTime);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: `0 20px 40px ${alpha(theme.palette.common.black, 0.15)}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 3,
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
              {isEditMode ? '医師シフト編集' : '医師シフト新規作成'}
            </Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Stack spacing={3}>
            {/* エラー表示 */}
            <AnimatePresence>
              {errors.general && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <Alert severity="error" sx={{ borderRadius: 2 }}>
                    {errors.general}
                  </Alert>
                </motion.div>
              )}
            </AnimatePresence>

            {/* 医師ID */}
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                <DoctorIcon sx={{ mr: 1, verticalAlign: 'middle' }} fontSize="small" />
                医師ID
              </Typography>
              <TextField
                fullWidth
                value={formData.doctor_id}
                onChange={(e) => handleFieldChange('doctor_id', e.target.value)}
                placeholder="例: DR001"
                error={!!errors.doctor_id}
                helperText={errors.doctor_id}
                InputProps={{
                  sx: { borderRadius: 2 }
                }}
              />
            </Box>

            <Divider />

            {/* シフト日付 */}
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                <DateIcon sx={{ mr: 1, verticalAlign: 'middle' }} fontSize="small" />
                シフト日付
              </Typography>
              <DatePicker
                value={formData.shift_date}
                onChange={(value) => handleFieldChange('shift_date', value)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!errors.shift_date,
                    helperText: errors.shift_date,
                    InputProps: {
                      sx: { borderRadius: 2 }
                    }
                  }
                }}
              />
            </Box>

            {/* 時間設定 */}
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                <TimeIcon sx={{ mr: 1, verticalAlign: 'middle' }} fontSize="small" />
                勤務時間
              </Typography>
              
              <Stack direction="row" spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    開始時間
                  </Typography>
                  <TimePicker
                    value={formData.start_time}
                    onChange={handleStartTimeChange}
                    ampm={false}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.start_time,
                        helperText: errors.start_time,
                        InputProps: {
                          sx: { borderRadius: 2 }
                        }
                      }
                    }}
                  />
                </Box>
                
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    終了時間
                  </Typography>
                  <TimePicker
                    value={formData.end_time}
                    onChange={(value) => handleFieldChange('end_time', value)}
                    ampm={false}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.end_time,
                        helperText: errors.end_time,
                        InputProps: {
                          sx: { borderRadius: 2 }
                        }
                      }
                    }}
                  />
                </Box>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 3, backgroundColor: alpha(theme.palette.background.default, 0.5) }}>
          <Button
            onClick={onClose}
            variant="outlined"
            disabled={isLoading}
            sx={{ borderRadius: 2 }}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? undefined : <SaveIcon />}
            sx={{ borderRadius: 2, minWidth: 120 }}
          >
            {isLoading ? '保存中...' : isEditMode ? '更新' : '作成'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};